import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { MenuButton, TitleWithPathForPdf, useScreenWidth } from "@msys/ui";
import { Divider, Stack } from "@mui/material";
import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  AskWhom,
  ItemType,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import { usePageWidth } from "../../commons/hooks/usePageWidth.js";
import { PageFullScreenModal } from "../../commons/layout/PageFullScreenModal.js";
import { PageMiddleColumn } from "../../commons/layout/PageMiddleColumn.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { REQUIREMENT_ITEM_TYPES } from "../../constants.js";
import { EmptyStateItem } from "../../features/doc-items/items/EmptyStateItem.js";
import { DocItemContextMenuItems } from "../../features/doc-items/menus/DocItemContextMenuItems.js";
import { ItemTypeMenuItem } from "../../features/doc-items/menus/ItemTypeMenuItem.js";
import { ProjectBuildingPlacementMenuItem } from "../../features/doc-items/menus/ProjectBuildingPlacementMenuItem.js";
import { ItemWizardButton } from "../../features/projects/requirement-trees.js";
import { RequirementItem } from "../../features/requirements/RequirementItem.js";
import { useProjectRequirementContext } from "./ProjectRequirementContext.js";
import { useProjectRequirementEditItemQuery } from "./ProjectRequirementEditItem.generated.js";

const REFETCH_QUERY = [namedOperations.Query.ProjectRequirementEditItem];

export function ProjectRequirementEditItem({
  projectId,
  requirementId,
  pathToDocPage,
  isAbsolute,
}: {
  projectId: string;
  requirementId: string;
  pathToDocPage: string;
  isAbsolute?: boolean;
}) {
  const { itemId } = useParams();
  if (!itemId) throw new Error("Requirement item id is missing");
  const navigate = useNavigate();

  const { isMinTablet } = useScreenWidth();
  const { isMinTwoColumnsWithPreview } = usePageWidth();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const { expandedItemIds, pasteItem, isAllowedToPasteItem } =
    useOutletContext<{
      expandedItemIds: string[] | undefined;
      pasteItem:
        | ((docId: string, parentItemId: string) => Promise<void>)
        | null;
      isAllowedToPasteItem: (parentItem: { type: ItemType }) => boolean;
      viewerDecisionRole: AskWhom;
    }>();

  const client = useApolloClient();
  const query = useProjectRequirementEditItemQuery({
    client,
    variables: { projectId, requirementId, itemId },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const requirement = getDataOrNull(query.data?.requirement)?.requirement;
  const item = requirement?.item;

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const { hasItemAnyQuestions, refetchWizardQuestions } =
    useProjectRequirementContext();

  if (!item || !requirement || !project)
    return (
      <EmptyStateItem
        isAbsolute={isAbsolute ?? !isMinTwoColumnsWithPreview}
        loading={query.loading}
        error={query.error}
        onCloseButtonClick={() => navigate(pathToDocPage, { replace: true })}
      />
    );

  const requirementItem = (
    <>
      <RequirementItem
        key={itemId} // force reset forms
        project={project}
        requirement={requirement}
        item={item}
        pathToDocPage={pathToDocPage}
        onUpdateDataRefetchQueries={REFETCH_QUERY}
      />
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );

  const title = (
    <TitleWithPathForPdf title={item.title} pathForPdf={item.pathForPdf} />
  );

  const headerActions = (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: "center",
      }}
    >
      {hasItemAnyQuestions(item.id) && (
        <ItemWizardButton
          projectId={projectId}
          requirement={requirement}
          item={item}
          refetchAfterApplyItemAction={refetchWizardQuestions}
        />
      )}
      <MenuButton onToggle={setIsOpen}>
        {/* we need to trigger refetch when menu gets opened */}
        {isOpen && (
          <ItemTypeMenuItem
            projectId={projectId}
            docId={requirementId}
            itemId={itemId}
            itemType={item.type}
            allowedTypes={REQUIREMENT_ITEM_TYPES}
            expandedItemIds={expandedItemIds}
          />
        )}
        <Divider />
        <ProjectBuildingPlacementMenuItem
          projectId={projectId}
          docId={requirementId}
          itemId={item.id}
        />
        <Divider />
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={requirementId}
          docType="REQUIREMENT"
          projectId={projectId}
          item={item}
          itemParentType={null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedItemIds}
          pasteItem={isAllowedToPasteItem(item) ? pasteItem : null}
          onAdditionalItemInputChange={null}
        />
      </MenuButton>
    </Stack>
  );

  if (isMinTablet) {
    return (
      <PageMiddleColumn
        key={itemId}
        hasRightBorder
        hasBackground
        isSelected
        isAbsolute={isAbsolute ?? !isMinTwoColumnsWithPreview}
        title={title}
        onCloseButtonClick={() => navigate(pathToDocPage, { replace: true })}
        headerActions={headerActions}
      >
        {requirementItem}
      </PageMiddleColumn>
    );
  }

  return (
    <PageFullScreenModal
      key={itemId}
      title={title}
      onCloseButtonClick={() => navigate(pathToDocPage, { replace: true })}
      headerActions={headerActions}
    >
      {requirementItem}
    </PageFullScreenModal>
  );
}
